import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AngaMark } from "@/components/brand/AngaMark";
import { persistSession, type DashboardAudience } from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search["token"] === "string" ? search["token"] : "",
    refresh: typeof search["refresh"] === "string" ? search["refresh"] : "",
    audience: search["audience"] === "admin" ? ("admin" as const) : ("user" as const),
    next: typeof search["next"] === "string" ? search["next"] : "",
    error: typeof search["error"] === "string" ? search["error"] : "",
  }),
  head: () => ({
    meta: [{ title: "Signing in…" }],
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { platformName } = usePlatformConfig();
  const { token, refresh, audience, next, error } = Route.useSearch();
  const [message, setMessage] = useState(t("auth.callbackWorking"));

  useEffect(() => {
    if (error || !token) {
      setMessage(error || t("common.errorGeneric"));
      const fallback = audience === "admin" ? "/admin/login" : "/login";
      const timer = window.setTimeout(() => {
        void navigate({ to: fallback });
      }, 1600);
      return () => window.clearTimeout(timer);
    }

    const aud: DashboardAudience = audience === "admin" ? "admin" : "user";
    persistSession(aud, { accessToken: token, refreshToken: refresh || null });
    const destination = next || (aud === "admin" ? "/admin" : "/app");
    window.location.replace(destination);
    return undefined;
  }, [audience, error, navigate, next, refresh, t, token]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#064E3B] text-white">
        <AngaMark className="h-7 w-7" title={platformName} />
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
